/**
 * Tenant-aware navigation hooks
 *
 * These hooks ensure the tenant query parameter is preserved
 * across all navigation in localhost development mode.
 */

import { useLocation } from "wouter";
import { useCallback } from "react";

/**
 * Get the current tenant from URL or localStorage
 */
export function getCurrentTenant(): string | null {
  // Check URL query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const tenantParam = urlParams.get("tenant");
  if (tenantParam) {
    return tenantParam;
  }

  // Check localStorage (for persisted tenant in development)
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname.match(/^[\d.]+$/)) {
    return localStorage.getItem("dev_tenant_override");
  }

  return null;
}

/**
 * Build a URL with tenant parameter preserved
 */
export function buildTenantUrl(path: string): string {
  const tenant = getCurrentTenant();

  // Only add tenant param in localhost development
  const hostname = window.location.hostname;
  if (!tenant || (hostname !== "localhost" && !hostname.match(/^[\d.]+$/))) {
    return path;
  }

  // Parse existing query params from path
  const [basePath, existingQuery] = path.split("?");
  const params = new URLSearchParams(existingQuery || "");

  // Add tenant if not already present
  if (!params.has("tenant")) {
    params.set("tenant", tenant);
  }

  const queryString = params.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
}

/**
 * Hook for tenant-aware navigation
 *
 * Usage:
 *   const { navigate, buildUrl } = useTenantNavigation();
 *   navigate("/app/agents"); // Will include ?tenant=elite if in dev mode
 */
export function useTenantNavigation() {
  const [location, setLocation] = useLocation();

  const navigate = useCallback(
    (path: string, options?: { replace?: boolean }) => {
      const url = buildTenantUrl(path);
      if (options?.replace) {
        window.history.replaceState(null, "", url);
      }
      setLocation(url);
    },
    [setLocation]
  );

  return {
    location,
    navigate,
    buildUrl: buildTenantUrl,
    currentTenant: getCurrentTenant(),
  };
}

/**
 * Get tenant-aware href for Link components
 *
 * Usage in components:
 *   <Link href={tenantHref("/app/agents")}>Agents</Link>
 */
export function tenantHref(path: string): string {
  return buildTenantUrl(path);
}

/**
 * Navigate using window.location.href with tenant preserved
 *
 * Usage:
 *   tenantRedirect("/app"); // Redirects to /app?tenant=elite in dev mode
 */
export function tenantRedirect(path: string): void {
  window.location.href = buildTenantUrl(path);
}

export default useTenantNavigation;
